import React, {useEffect, useState, ReactNode, useMemo} from "react"
import useAuth, {useAuthIsGhost, useIsLoggedIn} from "@irati/auth/helpers/auth/useAuth"
import {withRouter, RouteComponentProps} from "react-router-dom"
import Braze from "../services/braze"
import {BrazeLogger} from "../services/brazeLogger"
import {isLoggingEnabled} from "../utils/env"
import {ActionsContext, NotificationActionContext, NotificationContext, initial} from "./context"
import {brazeAdapter} from "./adapters/brazeAdapter"
import {dedupeTriggers} from "./dedupe"

interface Props extends RouteComponentProps {
  children: ReactNode
  logger: BrazeLogger
}

export const ContentCardProvider = withRouter<Props>(({children, logger, location}) => {
  const [contentCards, setContentCards] = useState(initial)

  const auth = useAuth()
  const isLoggedIn = useIsLoggedIn()
  const {isNotGhost} = useAuthIsGhost()

  const userId = auth?.user?.uuid || ""
  const subdomain = auth?.business?.subdomain || ""
  const canSeeNotifications = isLoggedIn && auth?.productPermissions?.platform?.contentCards && isNotGhost

  /** Subscribe to braze notifications and updates */
  useEffect(() => {
    let handler: string
    let ignore = false

    if (canSeeNotifications) {
      const loggingOn = isLoggingEnabled || ["home", "guild"].includes(subdomain)
      logger.enableLogging(loggingOn)

      handler = Braze.subscribe({
        userId,
        enableLogging: loggingOn,
        onCardUpdates: (cards: any) => {
          if (ignore) return
          setContentCards((oldCards) => {
            logger.log("cards updated")
            const newCards = brazeAdapter(cards, oldCards, logger)
            logger.debug({newCards})
            return {
              ...newCards,
              triggers: dedupeTriggers(newCards.triggers, Braze.markCardsAsDismissed)
            }
          })
        }
      })
    }

    return () => {
      ignore = true
      if (handler) Braze.unSubscribe(handler)
    }
  }, [canSeeNotifications, userId, subdomain, logger])

  /** resync the braze sdk cards with the context on any kind of user navigation */
  useEffect(() => {
    Braze.markCardsAsActioned()
  }, [location])

  const actions = useMemo(
    (): ActionsContext => ({
      // Native only
      clearPopup() {},
      showPopup() {},
      refresh() {},
      // shared
      markCardsAsActioned(_ids) {
        /**
         * NOTE right now we are just marking everything as actioned, but in
         * the future we could extend this if we need more control. The ids are
         * forced in the type signature to make this easier to alter in the
         * future
         */
        Braze.markCardsAsActioned()
      },
      markContentCardsDismissed(ids) {
        Braze.markCardsAsDismissed(ids)
        Braze.markCardsAsActioned()
      },
      markContentCardClicked(id, {actioned} = {actioned: false}) {
        Braze.markCardAsClicked(id)
        if (actioned) {
          Braze.markCardsAsActioned()
        }
      },
      markContentCardsImpression(ids) {
        Braze.markCardsAsSeen(ids)
      }
    }),
    []
  )

  return (
    <NotificationContext.Provider value={contentCards}>
      <NotificationActionContext.Provider value={actions}>{children}</NotificationActionContext.Provider>
    </NotificationContext.Provider>
  )
})
